import Phaser from 'phaser';
import { BaseGameScene } from './BaseGameScene';
import { HEADER_HEIGHT, PRE_OPEN_GAME_SECONDS } from '../config';
import { COLORS, DISPLAY_FONT, Radii } from '../ui/theme';
import { store } from '../state/store';
import { events } from '../state/events';
import { Button } from '../ui/Button';
import { customersSystem, hashId } from '../systems/CustomerSystem';
import type { Customer, MenuItem, ServeLogEntry, SceneName } from '../types';

// Front-of-house cafe. Pre-open: closed shop with a countdown. Open: the
// serve loop — customers queue up, the front one orders, the player clicks a
// display-case item to serve one unit (or Skip). Customers are code-drawn
// placeholder people (kid-drawn sprites replace them later).
const CUSTOMER_COLORS = [0xe8744f, 0x5b8dd9, 0x9a67c9, 0x4faf7e, 0xd95b9a, 0xc9a24f, 0x5fb8c9, 0x8f6f5a];

export class CounterScene extends BaseGameScene {
  private liveNodes: Phaser.GameObjects.GameObject[] = [];
  private unsubs: (() => void)[] = [];
  private caseArea!: { x: number; y: number; w: number; h: number };
  private counterY = 0;

  constructor() {
    super({ key: 'Counter' });
  }

  protected sceneName(): Extract<SceneName, 'Menu' | 'Kitchen' | 'Oven' | 'Counter'> { return 'Counter'; }
  protected title(): string { return ''; }
  protected backgroundColor(): number { return COLORS.bgCounter; }

  create(): void {
    this.setupChrome();
    const w = this.scale.width;
    const h = this.scale.height;

    // ── Static stage: back wall, floor, counter, baker ─────────────────────
    const wallY = HEADER_HEIGHT;
    const wallH = h * 0.52 - HEADER_HEIGHT;
    this.add.rectangle(0, wallY, w, wallH, 0xf5d6b8).setOrigin(0, 0);
    this.add.text(w / 2, wallY + 18, store.state.bakeryName, {
      fontFamily: DISPLAY_FONT, fontSize: '46px', fontStyle: '700', color: '#553089',
    }).setOrigin(0.5, 0);

    const floorY = h * 0.52;
    this.add.rectangle(0, floorY, w, h - floorY, 0xcfb98e).setOrigin(0, 0);

    this.counterY = h * 0.72;
    this.add.rectangle(0, this.counterY, w, 110, 0x8c5b2f).setOrigin(0, 0);
    this.add.rectangle(0, this.counterY, w, 14, 0xa97842).setOrigin(0, 0);

    const bakerKey = `baker-character1-${'abcdefghijklm'[store.state.bakerCostume] ?? 'a'}`;
    if (this.textures.exists(bakerKey)) {
      const baker = this.add.image(w * 0.10, this.counterY + 6, bakerKey).setOrigin(0.5, 1);
      baker.setScale((h * 0.30) / baker.height);
    }

    // Display case sits mid-counter — the baker stands to its left, the
    // customer queue forms on the right.
    this.caseArea = { x: w * 0.20, y: wallY + 88, w: w * 0.48, h: floorY - wallY - 100 };

    // ── Live layer ──────────────────────────────────────────────────────────
    this.renderLive();
    const sub = (fn: () => void) => fn;
    this.unsubs.push(events.on('phase:open', sub(() => this.renderLive())));
    this.unsubs.push(events.on('queue:advance', sub(() => this.renderLive())));
    this.unsubs.push(events.on('menu:changed', sub(() => this.renderLive())));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unsubs.forEach((u) => u()));
  }

  private clearLive(): void {
    this.liveNodes.forEach((n) => n.destroy());
    this.liveNodes = [];
  }

  private track<T extends Phaser.GameObjects.GameObject>(n: T): T {
    this.liveNodes.push(n);
    return n;
  }

  private renderLive(): void {
    this.clearLive();
    const phase = store.state.phase;

    this.renderCase(phase === 'open');

    if (phase === 'pre-open') {
      const w = this.scale.width;
      const remaining = Math.max(0, PRE_OPEN_GAME_SECONDS - store.state.clockGameSec);
      const mins = Math.ceil(remaining / 60);
      const g = this.track(this.add.graphics());
      g.fillStyle(0xffffff, 0.9);
      g.fillRoundedRect(w / 2 - 320, this.counterY + 20, 640, 70, Radii.md);
      this.track(this.add.text(w / 2, this.counterY + 55,
        `CLOSED — doors open in ${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}. Get baking!`, {
          fontFamily: DISPLAY_FONT, fontSize: '24px', fontStyle: '600', color: '#553089',
        }).setOrigin(0.5));
      // Done prepping early? Skip ahead to opening time.
      this.track(new Button(this, w / 2, this.counterY + 140, 'Open the Shop Now!', {
        fill: COLORS.accentGreen, hoverFill: 0x5fe28b, fontSize: '22px', width: 300, height: 54,
        onClick: () => store.openShopNow(),
      }));
      return;
    }

    if (phase === 'open') {
      this.renderQueue();

      const w = this.scale.width;
      const h = this.scale.height;
      if (store.activeCustomer) {
        this.track(new Button(this, w - 130, h - 50, 'Skip', {
          fill: 0xaaaaaa, hoverFill: 0xcccccc, fontSize: '20px', width: 140, height: 46,
          onClick: () => { store.skipCustomer(); },
        }));
      } else {
        this.track(this.add.text(w * 0.72, this.counterY - 60, 'All customers served! \u{1F389}', {
          fontFamily: DISPLAY_FONT, fontSize: '26px', fontStyle: '600', color: '#1f8b3a',
        }).setOrigin(0.5));
      }
      this.track(new Button(this, w - 320, h - 50, 'Close Early', {
        fill: COLORS.accentOrange, hoverFill: 0xffb24a, fontSize: '20px', width: 170, height: 46,
        onClick: () => store.closeShop(),
      }));
    }
  }

  // ── Display case ──────────────────────────────────────────────────────────

  private renderCase(interactive: boolean): void {
    const { x, y, w, h } = this.caseArea;
    const g = this.track(this.add.graphics());
    g.fillStyle(0xffffff, 0.95);
    g.fillRoundedRect(x, y, w, h, Radii.md);
    g.lineStyle(3, COLORS.headerPurple, 1);
    g.strokeRoundedRect(x, y, w, h, Radii.md);
    this.track(this.add.text(x + 18, y + 10, 'TODAY IN THE CASE', {
      fontFamily: DISPLAY_FONT, fontSize: '18px', fontStyle: '600', color: '#553089',
    }));

    const sellable = store.state.todaysMenu.filter((m) => m.status === 'ready' && m.quantity > 0);
    if (sellable.length === 0) {
      this.track(this.add.text(x + w / 2, y + h / 2, 'Nothing ready to sell yet', {
        fontFamily: DISPLAY_FONT, fontSize: '24px', fontStyle: 'italic', color: '#aaaaaa',
      }).setOrigin(0.5));
      return;
    }

    const slotW = (w - 40) / Math.max(sellable.length, 3);
    sellable.forEach((batch, i) => {
      const sx = x + 20 + i * slotW;
      this.renderCaseSlot(batch, sx, y + 40, slotW - 14, h - 56, interactive);
    });
  }

  private renderCaseSlot(batch: MenuItem, x: number, y: number, w: number, h: number, interactive: boolean): void {
    const recipe = store.recipeById(batch.recipe);
    const c = this.track(this.add.container(x + w / 2, y + h / 2));

    const g = this.add.graphics();
    g.fillStyle(0xfff7ec, 1);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, 10);
    g.lineStyle(2, COLORS.accentOrange, 1);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, 10);
    c.add(g);

    const color = batch.doneness === 'burnt' ? COLORS.burnt
                : batch.doneness === 'perfect' ? COLORS.perfect
                : COLORS.raw;
    const dot = this.add.graphics();
    dot.fillStyle(color, 1);
    dot.fillCircle(0, -h / 2 + 44, 28);
    c.add(dot);

    c.add(this.add.text(0, -h / 2 + 84, recipe?.name ?? batch.recipe, {
      fontFamily: DISPLAY_FONT, fontSize: '17px', fontStyle: '500', color: '#2a1f4a',
      align: 'center', wordWrap: { width: w - 16 },
    }).setOrigin(0.5, 0));
    c.add(this.add.text(0, h / 2 - 46, `× ${batch.quantity}`, {
      fontFamily: '"VT323", monospace', fontSize: '24px', color: '#55488a',
    }).setOrigin(0.5));
    c.add(this.add.text(0, h / 2 - 22, `${batch.price} coins`, {
      fontFamily: DISPLAY_FONT, fontSize: '15px', fontStyle: '600', color: '#c2571f',
    }).setOrigin(0.5));

    if (interactive && store.activeCustomer) {
      c.setInteractive(new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h), Phaser.Geom.Rectangle.Contains);
      c.input!.cursor = 'pointer';
      c.on('pointerover', () => c.setScale(1.04));
      c.on('pointerout', () => c.setScale(1));
      c.on('pointerup', () => this.serve(batch.id));
    }
  }

  // ── Customer queue ────────────────────────────────────────────────────────

  private renderQueue(): void {
    const w = this.scale.width;
    const ids = store.state.dayQueueIds;
    if (ids.length === 0) return;

    // Front customer at the counter, up to two waiting behind.
    const front = store.customerById(ids[0]);
    if (front) this.drawCustomer(front, w * 0.72, this.counterY + 4, 1, true);
    const second = ids[1] ? store.customerById(ids[1]) : undefined;
    if (second) this.drawCustomer(second, w * 0.85, this.counterY + 2, 0.72, false);
    const third = ids[2] ? store.customerById(ids[2]) : undefined;
    if (third) this.drawCustomer(third, w * 0.94, this.counterY, 0.55, false);

    if (ids.length > 3) {
      this.track(this.add.text(w * 0.94, this.counterY + 40, `+${ids.length - 3} more`, {
        fontFamily: DISPLAY_FONT, fontSize: '16px', fontStyle: 'italic', color: '#8a6f52',
      }).setOrigin(0.5, 0));
    }
  }

  private drawCustomer(customer: Customer, x: number, baseY: number, scale: number, isFront: boolean): void {
    const c = this.track(this.add.container(x, baseY));
    c.setScale(scale);

    const color = CUSTOMER_COLORS[hashId(customer.id) % CUSTOMER_COLORS.length];
    const g = this.add.graphics();
    // Body
    g.fillStyle(color, 1);
    g.fillRoundedRect(-42, -150, 84, 118, 22);
    // Head
    g.fillStyle(0xf2c9a0, 1);
    g.fillCircle(0, -182, 36);
    // Hair cap tinted by their color
    g.fillStyle(color, 1);
    g.beginPath();
    g.arc(0, -186, 37, Math.PI, 0, false);
    g.closePath();
    g.fillPath();
    c.add(g);

    // Initial on the chest + name below
    c.add(this.add.text(0, -92, customer.name[0] ?? '?', {
      fontFamily: DISPLAY_FONT, fontSize: '34px', fontStyle: '700', color: '#ffffff',
    }).setOrigin(0.5));
    c.add(this.add.text(0, -24, customer.name, {
      fontFamily: DISPLAY_FONT, fontSize: '17px', fontStyle: '600', color: '#553089',
    }).setOrigin(0.5, 0));

    if (isFront) {
      // Speech bubble with the order
      const text = customersSystem.orderText(customer);
      const bubble = this.add.container(0, -292);
      const t = this.add.text(0, 0, text, {
        fontFamily: DISPLAY_FONT, fontSize: '18px', fontStyle: '500', color: '#2a1f4a',
        align: 'center',
      }).setOrigin(0.5);
      const bw = t.width + 36;
      const bh = t.height + 24;
      const bg = this.add.graphics();
      bg.fillStyle(0xffffff, 0.97);
      bg.fillRoundedRect(-bw / 2, -bh / 2, bw, bh, 12);
      bg.lineStyle(2, COLORS.headerPurple, 1);
      bg.strokeRoundedRect(-bw / 2, -bh / 2, bw, bh, 12);
      bg.fillStyle(0xffffff, 0.97);
      bg.fillTriangle(-10, bh / 2 - 1, 10, bh / 2 - 1, 0, bh / 2 + 14);
      bubble.add([bg, t]);
      c.add(bubble);
    }
  }

  // ── Serving ───────────────────────────────────────────────────────────────

  private serve(batchId: string): void {
    const customerX = this.scale.width * 0.72;
    const entry = store.serveCustomer(batchId);
    if (!entry) return;
    this.showReaction(entry, customerX, this.counterY - 220);
  }

  private showReaction(entry: ServeLogEntry, x: number, y: number): void {
    const r = customersSystem.reactionFor(entry);
    const t = this.add.text(x, y, `${r.emoji}  ${r.text}`, {
      fontFamily: DISPLAY_FONT, fontSize: '30px', fontStyle: '700', color: r.color,
      stroke: '#ffffff', strokeThickness: 5,
    }).setOrigin(0.5).setDepth(20);
    this.tweens.add({
      targets: t,
      y: y - 70,
      alpha: { from: 1, to: 0 },
      duration: 1600,
      ease: 'Cubic.easeOut',
      onComplete: () => t.destroy(),
    });

    if (entry.coinsEarned > 0) {
      const coin = this.add.text(x, y + 44, `+${entry.coinsEarned} \u{1FA99}`, {
        fontFamily: '"VT323", monospace', fontSize: '30px', color: '#c2871f',
        stroke: '#ffffff', strokeThickness: 4,
      }).setOrigin(0.5).setDepth(20);
      this.tweens.add({
        targets: coin,
        y: y - 20,
        alpha: { from: 1, to: 0 },
        duration: 1400,
        delay: 150,
        ease: 'Cubic.easeOut',
        onComplete: () => coin.destroy(),
      });
    }
  }
}
